import { get, map, find } from 'lodash-es';
import debug from 'debug';
import { Application } from './application';
import { User } from './schemas';

const log = debug('itp:core:users');

function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache = new Map<string, ReturnType<T>>();
  return function (this: any, ...args: Parameters<T>): ReturnType<T> {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  } as T;
}

export class Users {
  protected users: User[];
  readonly app: Application;
  readonly filters: Dictionary<Function>;
  private _getByIdMemo: (id: string) => User | undefined;
  private _getUserDisplayNameMemo: (id: string) => string;

  constructor(app: Application) {
    this.users = [];
    this.app = app;
    this.filters = userFilters(this);
    this._getByIdMemo = memoize((id: string) => find(this.users, { id }));
    this._getUserDisplayNameMemo = memoize((id: string) => {
      const user = this.getById(id);
      return user ? user.fullname || user.username || user.email || user.id : id;
    });
  }

  getById(id: string) {
    return this._getByIdMemo(id);
  }

  getUserDisplayName(id: string) {
    return this._getUserDisplayNameMemo(id);
  }

  async load() {
    const cfg = this.app.spec.config;
    if (this.app.isFakeAuth || (cfg && cfg.noLoadUsers)) {
      return Promise.resolve();
    }

    try {
      return this.app.connections
        .getConnection('default')
        .request('list_users')
        .then((result) => {
          this.users = map(result.data, mapUserData);
        });
    } catch (error) {
      log('Failed to load users - %o', error);
    }
  }
}

function mapUserData(data: object): User {
  const id: any = get(data, 'id'),
    email: any = get(data, 'email'),
    username = get(data, 'username'),
    enabled = get(data, 'enabled') || false,
    firstname = get(data, 'firstname'),
    lastname = get(data, 'lastname'),
    fullname = `${firstname} ${lastname}`.trim();

  return {
    id,
    email,
    username,
    enabled,
    fullname,
  };
}

function userFilters(users: Users) {
  return {
    username(value: string) {
      return users.getUserDisplayName(value);
    },
  };
}
